import { useEffect, useState } from "react";
import { ArrowsClockwise, GlobeHemisphereWest, Monitor, User, Warning, CheckCircle, MapPin, MagnifyingGlass } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { formatLocal } from "@/lib/timezone";

/**
 * Login Activity tab — internal staff only.
 *
 * Two sub-tabs:
 *   • Users          — who has onboarded for this client + last-login summary
 *   • Login History  — every login attempt (success + failure), enriched with
 *                      reverse-DNS hostname + ip-api.com geolocation
 */
export default function ClientLoginActivityTab({ clientId }) {
  const [sub, setSub] = useState("users"); // "users" | "events"
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [includeFailed, setIncludeFailed] = useState(true);

  useEffect(() => {
    if (sub === "users") loadUsers();
    if (sub === "events") loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub, includeFailed, clientId]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/tenants/${clientId}/login-activity/users`);
      setUsers(res.data.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    setLoading(true);
    try {
      const res = await api.get(
        `/tenants/${clientId}/login-activity/events?limit=200&include_failed=${includeFailed ? "true" : "false"}`,
      );
      setEvents(res.data.events || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load login events");
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter((u) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q);
  });

  const filteredEvents = events.filter((e) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (e.user_email || "").toLowerCase().includes(q) ||
      (e.user_name || "").toLowerCase().includes(q) ||
      (e.ip || "").toLowerCase().includes(q) ||
      (e.geo?.city || "").toLowerCase().includes(q) ||
      (e.geo?.country || "").toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="login-activity-tab">
      {/* Sub-tabs */}
      <div className="flex border-b border-[#E5E5E5] mb-4">
        <button
          onClick={() => setSub("users")}
          className={`px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 border-b-2 transition-colors ${
            sub === "users" ? "border-[#0055FF] text-[#0055FF]" : "border-transparent text-[#525252] hover:text-[#0A0A0A]"
          }`}
          data-testid="login-activity-subtab-users"
        >
          <User size={13} /> Users
        </button>
        <button
          onClick={() => setSub("events")}
          className={`px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 border-b-2 transition-colors ${
            sub === "events" ? "border-[#0055FF] text-[#0055FF]" : "border-transparent text-[#525252] hover:text-[#0A0A0A]"
          }`}
          data-testid="login-activity-subtab-events"
        >
          <ArrowsClockwise size={13} /> Login History
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1 max-w-xs">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#737373]" />
          <Input
            placeholder={sub === "users" ? "Search by name or email" : "Search by user, IP, city, country"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-xs rounded-none border-[#E5E5E5]"
            data-testid="login-activity-search"
          />
        </div>
        {sub === "events" && (
          <label className="flex items-center gap-1.5 text-xs text-[#525252]" data-testid="login-activity-include-failed">
            <input
              type="checkbox"
              checked={includeFailed}
              onChange={(e) => setIncludeFailed(e.target.checked)}
              className="accent-[#0055FF]"
            />
            Include failed attempts
          </label>
        )}
        <Button
          onClick={() => (sub === "users" ? loadUsers() : loadEvents())}
          variant="outline"
          className="h-8 px-3 text-xs rounded-none border-[#E5E5E5]"
          data-testid="login-activity-refresh"
        >
          <ArrowsClockwise size={12} className="mr-1.5" />
          Refresh
        </Button>
      </div>

      {/* Body */}
      {loading && <div className="text-xs text-[#737373] py-6 text-center">Loading…</div>}

      {sub === "users" && !loading && (
        <UsersTable users={filteredUsers} />
      )}

      {sub === "events" && !loading && (
        <EventsTable events={filteredEvents} />
      )}
    </div>
  );
}

function UsersTable({ users }) {
  if (users.length === 0) {
    return (
      <div className="text-center py-12 text-sm text-[#737373] border border-dashed border-[#E5E5E5]">
        No client users yet.
      </div>
    );
  }
  return (
    <div className="border border-[#E5E5E5] overflow-hidden" data-testid="login-activity-users-table">
      <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1.6fr)_120px_140px_minmax(0,1.6fr)_minmax(0,1.6fr)] bg-[#FAFAFA] border-b border-[#E5E5E5] text-[10px] font-bold uppercase tracking-wider text-[#525252]">
        <Cell>Name</Cell>
        <Cell>Email</Cell>
        <Cell>Status</Cell>
        <Cell>Onboarded</Cell>
        <Cell>Last Login</Cell>
        <Cell>From</Cell>
      </div>
      {users.map((u) => (
        <div
          key={u.id}
          className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1.6fr)_120px_140px_minmax(0,1.6fr)_minmax(0,1.6fr)] border-b border-[#E5E5E5] last:border-b-0 text-xs text-[#0A0A0A]"
          data-testid={`login-activity-user-row-${u.id}`}
        >
          <Cell><span className="font-semibold truncate">{u.name || "—"}</span></Cell>
          <Cell><span className="truncate">{u.email}</span></Cell>
          <Cell><StatusBadge status={u.status} /></Cell>
          <Cell>
            {u.onboarded ? (
              <span className="inline-flex items-center gap-1 text-[#16A34A]"><CheckCircle size={12} weight="fill" />Yes</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[#737373]"><Warning size={12} />Pending</span>
            )}
          </Cell>
          <Cell>
            {u.last_login_at ? (
              <span className="font-mono text-[11px] text-[#525252]">{formatLocal(u.last_login_at)}</span>
            ) : (
              <span className="text-[#A3A3A3]">never</span>
            )}
          </Cell>
          <Cell>
            <GeoBadge ip={u.last_login_ip} geo={u.last_login_geo} />
          </Cell>
        </div>
      ))}
    </div>
  );
}

function EventsTable({ events }) {
  if (events.length === 0) {
    return (
      <div className="text-center py-12 text-sm text-[#737373] border border-dashed border-[#E5E5E5]">
        No login events yet.
      </div>
    );
  }
  return (
    <div className="border border-[#E5E5E5] overflow-hidden" data-testid="login-activity-events-table">
      <div className="grid grid-cols-[160px_minmax(0,1.4fr)_140px_minmax(0,1.4fr)_minmax(0,1.6fr)_minmax(0,1.6fr)] bg-[#FAFAFA] border-b border-[#E5E5E5] text-[10px] font-bold uppercase tracking-wider text-[#525252]">
        <Cell>When</Cell>
        <Cell>User</Cell>
        <Cell>Result</Cell>
        <Cell>IP</Cell>
        <Cell>Location</Cell>
        <Cell>Browser</Cell>
      </div>
      {events.map((e) => (
        <div
          key={e.activity_id}
          className="grid grid-cols-[160px_minmax(0,1.4fr)_140px_minmax(0,1.4fr)_minmax(0,1.6fr)_minmax(0,1.6fr)] border-b border-[#E5E5E5] last:border-b-0 text-xs text-[#0A0A0A]"
          data-testid={`login-activity-event-row-${e.activity_id}`}
        >
          <Cell><span className="font-mono text-[11px] text-[#525252]">{formatLocal(e.created_at)}</span></Cell>
          <Cell>
            <div className="flex flex-col min-w-0">
              <span className="font-semibold truncate">{e.user_name || "—"}</span>
              <span className="text-[10px] text-[#737373] truncate">{e.user_email}</span>
            </div>
          </Cell>
          <Cell><EventTypeBadge type={e.activity_type} success={e.success} /></Cell>
          <Cell>
            <div className="flex flex-col min-w-0">
              <span className="font-mono text-[11px]">{e.ip || "—"}</span>
              {e.geo?.hostname && (
                <span className="text-[10px] text-[#737373] truncate" title={e.geo.hostname}>{e.geo.hostname}</span>
              )}
            </div>
          </Cell>
          <Cell><GeoBadge ip={e.ip} geo={e.geo} /></Cell>
          <Cell><UAChip ua={e.user_agent} /></Cell>
        </div>
      ))}
    </div>
  );
}

function Cell({ children }) {
  return <div className="px-3 py-2.5 flex items-center min-w-0">{children}</div>;
}

function StatusBadge({ status }) {
  const map = {
    active: "bg-[#DCFCE7] text-[#15803D] border-[#86EFAC]",
    disabled: "bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]",
    pending: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  };
  const cls = map[(status || "").toLowerCase()] || "bg-[#F5F5F5] text-[#525252] border-[#E5E5E5]";
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border ${cls}`}>{status || "—"}</span>;
}

function EventTypeBadge({ type, success }) {
  const cls = success
    ? "bg-[#DCFCE7] text-[#15803D] border-[#86EFAC]"
    : "bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]";
  const label =
    type === "login_success_password" ? "Login OK" :
    type === "login_success_google" ? "Google OK" :
    type === "login_failed" ? "Failed" :
    type === "login_blocked_disabled" ? "Disabled" :
    type === "login_blocked_domain" ? "Blocked Domain" :
    type;
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border ${cls}`}>{label}</span>;
}

function GeoBadge({ ip, geo }) {
  if (!ip) return <span className="text-[#A3A3A3] text-[11px]">—</span>;
  if (geo?.private) return <span className="text-[#A3A3A3] text-[11px]">Internal network</span>;
  const place = [geo?.city, geo?.region, geo?.country].filter(Boolean).join(", ");
  return (
    <div className="flex flex-col min-w-0" title={geo?.isp || ""}>
      <span className="inline-flex items-center gap-1 truncate">
        <MapPin size={11} className="text-[#525252] shrink-0" />
        <span className="truncate">{place || "Unknown"}</span>
      </span>
      {geo?.isp && (
        <span className="inline-flex items-center gap-1 text-[10px] text-[#737373] truncate">
          <GlobeHemisphereWest size={10} className="shrink-0" />
          <span className="truncate">{geo.isp}</span>
        </span>
      )}
    </div>
  );
}

function UAChip({ ua }) {
  if (!ua) return <span className="text-[#A3A3A3] text-[11px]">—</span>;
  const browser =
    /Edg\//.test(ua) ? "Edge" :
    /Chrome\//.test(ua) ? "Chrome" :
    /Firefox\//.test(ua) ? "Firefox" :
    /Safari\//.test(ua) ? "Safari" :
    "Other";
  const os =
    /Windows NT/.test(ua) ? "Windows" :
    /Mac OS X/.test(ua) ? "macOS" :
    /Linux/.test(ua) ? "Linux" :
    /iPhone|iPad/.test(ua) ? "iOS" :
    /Android/.test(ua) ? "Android" :
    "Other";
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-[#525252]" title={ua}>
      <Monitor size={11} />
      <span className="truncate">{browser} · {os}</span>
    </span>
  );
}
